import React, { useEffect, useRef } from 'react'
import { Hook } from '../hooks/Hook'
import { useSingletonMVUReducer } from '../mvuReducer'
import {
  Dispatch,
  WebRecorder2State,
  WebRecorder2Action,
  WebRecorder2SideEffect,
  subjectId,
  SetSubjectId,
} from './webRecorder2'
import { Loading } from '~/components/Loading'
import * as PubSub from './pubSub'
import { AudioInputSelector } from './ui/AudioInputSelector'
import { DownloadButton } from './ui/DownloadButton'
import { UploadProgress } from './ui/UploadProgress'
import { PlayingDisclosureView } from './ui/PlayingDisclosureView'
import { InitialView, RecordingView, RecordingPausedView, RecordingCountdownView } from './ui/MeetingDetailViews'
import { Button } from '~/components/Button'

export function WebRecorder2MeetingDetail({ hook }: { hook: Hook }) {
  const canUseWebRecorder = hook.el.hasAttribute('data-can-use-web-recorder')
  const canDownload = hook.el.hasAttribute('data-can-download')
  const byoProcessingMessage = hook.el.dataset.byoProcessingMessage
  const maxRecordingDurationSeconds = parseInt(hook.el.dataset.maxRecordingDurationSeconds || '14400', 10)

  const processingError = hook.el.dataset.processingError
  // TODO: Move processing state into webRecorder2 state
  const isProcessing = hook.el.hasAttribute('data-is-processing')
  const otherRemoteClientIsRecording = hook.el.hasAttribute('data-other-client-is-recording')

  const detailSubjectId = canUseWebRecorder
    ? (hook.el.dataset.meetingId ?? hook.el.dataset.integrationReferenceId)
    : undefined

  const [state, dispatch] = useSingletonMVUReducer<WebRecorder2State, WebRecorder2Action, WebRecorder2SideEffect>(
    'webRecorder2'
  )

  useEffect(() => {
    // If we're already recording, we don't want to stop the prior recording.
    const alreadyRecording = state && ['countdown', 'playing-disclosure', 'recording'].includes(state.mode)

    if (canUseWebRecorder && detailSubjectId && !alreadyRecording) {
      const action: SetSubjectId = {
        action: 'set-subject-id',
        subjectId: detailSubjectId,
        startRecording: window.location.search.includes('capture_now=true') ? 'ifNoPreviousRecordingExists' : 'no',
      }

      PubSub.publish('apply-action', action)
      PubSub.publish('apply-action', { action: 'set-max-duration', maxDurationSeconds: maxRecordingDurationSeconds })
      PubSub.publish('is-recordable', { subjectId: detailSubjectId })
    }
  }, [detailSubjectId, maxRecordingDurationSeconds])

  useEffect(() => {
    if (detailSubjectId && processingError) {
      PubSub.publish('apply-action', {
        action: 'handle-error-message',
        message: processingError,
        detailSubjectId,
      })
    }
  }, [detailSubjectId, processingError])

  const currentSubject = subjectId(state)
  const recordingAnotherMeeting = currentSubject && currentSubject !== detailSubjectId

  const renderCount = useRef(0)
  const hadAChanceToSetSubjectId = renderCount.current > 1

  useEffect(() => {
    if (recordingAnotherMeeting && hadAChanceToSetSubjectId) {
      debugLog('Capturing another meeting:', currentSubject, 'vs.', detailSubjectId)
    }
  }, [recordingAnotherMeeting, hadAChanceToSetSubjectId])

  renderCount.current += 1

  if (!state) return <Loading size="sm" />
  if (!canUseWebRecorder) return null

  const showInputSelector = ['recording', 'paused', 'countdown'].includes(state.mode) && !state.errorMessage
  const showSubmitButton = !isProcessing && state.mode === 'paused'

  return (
    <div className="flex flex-row gap-2 items-center">
      {byoProcessingMessage ? (
        <Loading size="sm">{byoProcessingMessage}</Loading>
      ) : recordingAnotherMeeting ? (
        <Loading size="sm">You’re capturing another meeting&hellip;</Loading>
      ) : state.errorMessage ? (
        <div className="text-xs text-danger text-center pt-2">{state.errorMessage || processingError}</div>
      ) : isProcessing && state.mode !== 'complete' ? (
        <Loading size="sm">Processing audio&hellip;</Loading>
      ) : state.otherLocalClientIsRecording && state.mode !== 'recording' ? (
        <Loading size="sm">You’re capturing a meeting in another tab&hellip;</Loading>
      ) : otherRemoteClientIsRecording && state.mode !== 'recording' ? (
        <Loading size="sm">Another browser is capturing this meeting&hellip;</Loading>
      ) : (
        <>
          {showSubmitButton && (
            <Button
              onClick={() => dispatch({ action: 'submit-recording' })}
              disabled={!state.isOnline}
              variant="primary"
              size="md"
              tooltip={
                state.isOnline
                  ? 'Upload the captured meeting now'
                  : 'Your computer does not appear to be connected to the internet.'
              }
            >
              Submit
            </Button>
          )}
          {state.microphoneInputState && showInputSelector && (
            <AudioInputSelector
              state={state.microphoneInputState}
              dispatch={dispatch}
              variant="icon-only"
              align="end"
            />
          )}
          <MeetingDetailBody state={state} dispatch={dispatch} canDownload={canDownload} />
        </>
      )}
    </div>
  )
}

function MeetingDetailBody({
  state,
  canDownload,
  dispatch,
}: {
  state: WebRecorder2State
  canDownload: boolean
  dispatch: Dispatch
}): React.ReactElement {
  switch (state.mode) {
    case 'initial':
      return <Loading size="sm" />
    case 'initialized':
      return state.initializingStream ? <Loading size="sm" /> : <InitialView dispatch={dispatch} />
    case 'playing-disclosure':
      return <PlayingDisclosureView />
    case 'recording':
      return <RecordingView state={state} dispatch={dispatch} />
    case 'paused':
      return <RecordingPausedView state={state} dispatch={dispatch} canDownload={canDownload} />
    case 'countdown':
      return <RecordingCountdownView state={state} />
    case 'uploading':
      return (
        <>
          {canDownload && <DownloadButton onClick={() => dispatch({ action: 'download-recording' })} />}
          <UploadProgress progressPercent={state.progressPercent} />
        </>
      )
    case 'complete':
      return (
        <div className="flex flex-row gap-2 items-center">
          {canDownload && <DownloadButton onClick={() => dispatch({ action: 'download-recording' })} />}
          Processing&hellip;
        </div>
      )
  }
}

export function durationDisplayMinWidthClass(durationSeconds: number) {
  const wholeMinutes = Math.floor(durationSeconds / 60)

  if (wholeMinutes < 10) {
    return 'min-w-8'
  } else if (wholeMinutes < 60) {
    return 'min-w-10'
  } else if (wholeMinutes < 120) {
    return 'min-w-12'
  } else {
    return 'min-w-14'
  }
}

function debugLog(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.log('[MeetingDetail]', ...args)
}
